import React from "react";
import CustomModal from "../../../ui/CustomModal/CustomModal";
import CustomBadge from "../../../ui/CustomBadge/CustomBadge";
import {
  TRANSACTION_STATUS_MAP,
  SERVICE_CODE_MAP,
} from "../../constants/transactionConstants";
import {
  extractRecipientName,
  getPaymentMethod,
} from "../../utils/transactionFormatters";
import {
  formatDateTimeToLocale,
  formatKES,
  getDisplayValue,
} from "../../../../utils/transactionutils";

const TransactionDetailsModal = ({
  opened,
  onClose,
  transactionData: {
    status,
    transactionDate,
    gatewayReference,
    recipientPartyName,
    accountNumber,
    description,
    amount,
    walletAmount,
    transactionCharges,
    taxes,
    walletRunningBalance,
    serviceCode,
    payoutAccountType,
    merchantTransactionReference,
    corePostingReference,
  } = {},
}) => {
  const isInflow = SERVICE_CODE_MAP.inflow.includes(serviceCode);
  const backendStatus = status;
  const mappedStatus = TRANSACTION_STATUS_MAP[backendStatus] || "default";

  const rows = [
    {
      label: isInflow ? "Paid By" : "Sent To",
      value: extractRecipientName(recipientPartyName),
    },
    { label: "Account Number", value: getDisplayValue(accountNumber) },
    {
      label: "Description",
      value: getDisplayValue(description),
      fullText: true,
    },
    { label: "Amount", value: formatKES(amount) },
    { label: "Transfer Fee", value: formatKES(transactionCharges) },
    { label: "Taxes", value: formatKES(taxes) },
    {
      label: isInflow ? "Credited to Wallet" : "Debited from Wallet",
      value: formatKES(walletAmount),
    },
    { label: "Wallet Balance After", value: formatKES(walletRunningBalance) },
    {
      label: "Payment Method",
      value: getPaymentMethod(serviceCode, payoutAccountType, gatewayReference),
    },
    {
      label: "Merchant Reference",
      value: getDisplayValue(merchantTransactionReference),
    },
    { label: "Core Reference", value: getDisplayValue(corePostingReference) },
  ];

  return (
    <CustomModal
      opened={opened}
      onClose={onClose}
      centered
      overlayProps={{ backgroundOpacity: 0.4, blur: 4 }}
      radius="md"
      size="lg"
      fullScreen={false}
      styles={{ body: { padding: 0 } }}
    >
      <div
        style={{
          maxHeight: "calc(90dvh - 160px)",
          overflowY: "auto",
          padding: "0 1rem 1rem",
        }}
      >
        <div className="w-full space-y-4 sm:space-y-6">
          <div className="text-center border-b pb-3 sm:pb-4">
            <h1 className="text-base sm:text-xl font-semibold text-blue-700">
              Transaction Details
            </h1>
            <p className="text-gray-500 text-xs sm:text-sm mt-1">
              {formatDateTimeToLocale(transactionDate)}
            </p>
          </div>

          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 bg-gray-50 p-3 sm:p-4 rounded-md">
            <div className="flex flex-col sm:flex-row sm:items-center sm:gap-2 min-w-0">
              <span className="text-xs text-gray-500 uppercase tracking-wide shrink-0">
                Gateway Reference:
              </span>
              <span className="text-xs sm:text-sm font-medium text-gray-900 truncate">
                {getDisplayValue(gatewayReference)}
              </span>
            </div>

            <CustomBadge color={mappedStatus} size="sm">
              {backendStatus || "UNKNOWN"}
            </CustomBadge>
          </div>

          <dl className="space-y-2.5 sm:space-y-3">
            {rows.map(({ label, value, fullText }) => (
              <div
                key={label}
                className="flex flex-col sm:flex-row sm:items-baseline gap-0.5 sm:gap-2"
              >
                <dt className="text-xs sm:text-sm text-gray-500 font-semibold sm:w-44 shrink-0">
                  {label}:
                </dt>
                <dd
                  className={
                    fullText
                      ? "text-sm sm:text-base text-gray-900 whitespace-normal break-words"
                      : "text-sm sm:text-base text-gray-900 truncate"
                  }
                >
                  {value}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </CustomModal>
  );
};

export default TransactionDetailsModal;
